import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";

/**
 * One slim row showing the next 4 days as small chips.
 * Shared by home-base strip, out-of-town project chip row, and works programme rows.
 */
export default function WeatherChipRow({ label, days, testid = "weather-chip-row" }) {
  if (!days || days.length === 0) {
    return (
      <div className="text-xs opacity-60" data-testid={`${testid}-empty`}>
        <span className="text-silver">{label}</span> — no forecast
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
      {label && <div className="text-xs text-silver mr-1 whitespace-nowrap">{label}</div>}
      {days.slice(0, 4).map((d, i) => {
        const bg = d.bad_weather ? "bg-amber-500/15 border-amber-400"
                 : !d.concrete_pour_ok ? "bg-red-500/10 border-red-500/70"
                 : "bg-neutral-900 border-neutral-800";
        return (
          <div key={d.date || i} data-testid={`chip-${d.date}`}
               title={`${fmtDate(d.date)} · ${d.rain_prob ?? 0}% rain · ${d.wind_max_kmh ?? 0} km/h${!d.concrete_pour_ok ? ' · not suitable for concrete pour' : ''}`}
               className={`flex items-center gap-1 px-2 py-1 border rounded text-[11px] ${bg}`}>
            <span className="opacity-70">{(d.date || "").slice(8, 10)}/{(d.date || "").slice(5, 7)}</span>
            <span className="text-silver">{Math.round(d.temp_max ?? 0)}°</span>
            <span className="opacity-60">·</span>
            <span>{d.rain_prob ?? 0}%</span>
            {i === 0 && d.bad_weather && (
              <span className="badge badge-amber ml-1 text-[10px]" data-testid={`chip-bad-${d.date}`}>Bad</span>
            )}
            {i === 0 && !d.concrete_pour_ok && (
              <span className="badge badge-red ml-1 text-[10px]" data-testid={`chip-no-pour-${d.date}`}>No pour</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function HomeBaseWeather({ testid = "home-base-weather" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/home-base/weather")
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Weather fetch failed"));
  }, []);
  if (err) return <div className="text-xs opacity-60" data-testid={`${testid}-err`}>{err}</div>;
  if (!data) return <div className="text-xs opacity-60" data-testid={`${testid}-loading`}>Loading forecast…</div>;
  const hb = data.home_base || {};
  const label = `${hb.town || "Home"}: today and next 3 days`;
  return (
    <div data-testid={testid}>
      <WeatherChipRow label={label} days={data.days || []} testid={`${testid}-row`}/>
    </div>
  );
}

export function OutOfTownProjectWeather({ projectId, testid }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/weather`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || ""));
  }, [projectId]);
  if (err) return <div className="text-xs opacity-60" data-testid={`${testid}-err`}>{err}</div>;
  if (!data) return <div className="text-xs opacity-60" data-testid={`${testid}-loading`}>Loading forecast…</div>;
  if (!data.outside_home_base) return null; // Guard: nothing to show for in-town sites
  if (!data.geocoded) {
    return <div className="text-xs opacity-70" data-testid={`${testid}-missing`}>{data.note || "No forecast"}</div>;
  }
  return (
    <WeatherChipRow label={`Site: ${data.geocoded.label || data.address} (next 4 days)`}
                     days={data.days || []} testid={testid}/>
  );
}
